import { unstopped, type Account, type Role } from '@/data/types'
import { STOP_COPY } from '@/lib/status'

/** Hierarchy AgentNode / AgentNodeWithBalance (OpenAPI). */
export interface AgentNode {
  id: string
  parentId: string | null
  name: string
  level: number
  creditLimit: string
  shareBp: number
  commissionBp?: number
  tbAccountId?: string
  balance?: string
}

/** Minor-unit string → major units (₹). */
export function fromMinorUnits(minor: string | undefined): number {
  if (!minor) return 0
  const n = Number(minor)
  if (!Number.isFinite(n) || n < 0) return 0
  return n / 100
}

/** OpenAPI level 1…6 → console Role (Winner7 ladder labels). */
export function levelToRole(level: number): Role {
  const map: Record<number, Role> = {
    1: 'company-master',
    2: 'triple-master',
    3: 'double-master',
    4: 'super-master',
    5: 'master',
    6: 'player',
  }
  return map[level] ?? 'master'
}

/** Map hierarchy node → console Account (fields the Create wizard needs). */
export function agentNodeToAccount(node: AgentNode): Account {
  const sharePoints = (node.shareBp ?? 0) / 100
  const commissionPct = (node.commissionBp ?? 0) / 100
  const role = levelToRole(node.level)
  return {
    id: node.id,
    username: node.name,
    displayName: node.name,
    role,
    parentId: node.parentId,
    path: node.parentId ? [node.parentId] : [],
    status: 'active',
    createdAt: new Date().toISOString(),
    lastActiveAt: null,
    creditLimit: fromMinorUnits(node.creditLimit),
    balance: fromMinorUnits(node.balance),
    exposure: 0,
    settlement: 0,
    share: { sportsbook: sharePoints, casino: sharePoints },
    commission: { fancy: commissionPct, bookmaker: commissionPct, matchOdds: commissionPct },
    betLimits: { min: 100, max: 100_000 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
      premiumSportsbook: true,
    },
    childCount: 0,
    memberCount: 0,
    flags: [],
    // The hierarchy service keeps no suspensions, so the honest projection is
    // the node's own status and no control at all.
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}
